/**
 * Canonical URL helpers used across API routes and PDF generators.
 *
 * Precedence for base URL resolution:
 * 1) NEXT_PUBLIC_SITE_URL
 * 2) NEXT_PUBLIC_APP_URL
 * 3) APP_URL
 * 4) VERCEL_URL (https://<vercel-host>)
 * 5) Request headers (x-forwarded-proto + x-forwarded-host, else host)
 * 6) Development fallback: http://localhost:3000 (never in production)
 */

export function normalizeBaseUrl(value: string): string {
  return value.replace(/\/$/, '');
}

const SEO_CANONICAL_PRODUCTION_URL = 'https://www.scopax.com';

function parseUrl(value: string): URL | null {
  try {
    return new URL(value);
  } catch {
    return null;
  }
}

function getEnvBaseUrlCandidate(): string | undefined {
  const envCandidates: Array<string | undefined> = [
    process.env.NEXT_PUBLIC_SITE_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.APP_URL,
  ];

  return envCandidates.find((v) => typeof v === 'string' && v.trim().length > 0);
}

/**
 * Strict canonical URL for public SEO surfaces (sitemap, robots, metadata).
 * Does not fall back to VERCEL_URL to prevent accidental canonical leakage.
 */
export function getSeoCanonicalBaseUrl(): string {
  const envBase = getEnvBaseUrlCandidate();
  const envUrl = envBase ? parseUrl(envBase) : null;

  if (process.env.NODE_ENV === 'production') {
    if (!envUrl) {
      return SEO_CANONICAL_PRODUCTION_URL;
    }

    const host = envUrl.hostname.toLowerCase();
    const invalidHost =
      host.includes('.vercel.app') ||
      host === 'localhost' ||
      host === '127.0.0.1' ||
      host === '::1';

    if (invalidHost || envUrl.protocol !== 'https:') {
      return SEO_CANONICAL_PRODUCTION_URL;
    }

    if (host === 'scopax.com' || host === 'www.scopax.com') {
      return SEO_CANONICAL_PRODUCTION_URL;
    }

    return normalizeBaseUrl(envUrl.toString());
  }

  if (envUrl) {
    return normalizeBaseUrl(envUrl.toString());
  }

  return 'http://localhost:3000';
}

export function getCanonicalBaseUrl(req?: { headers?: Headers | Record<string, string | string[] | undefined> } | null): string {
  const envCandidates: Array<string | undefined> = [
    process.env.NEXT_PUBLIC_SITE_URL,
    process.env.NEXT_PUBLIC_APP_URL,
    process.env.APP_URL,
    process.env.VERCEL_URL ? `https://${process.env.VERCEL_URL}` : undefined,
  ];

  const envBase = envCandidates.find((v) => typeof v === 'string' && v.trim().length > 0);
  if (envBase) {
    return normalizeBaseUrl(envBase);
  }

  // Attempt to build from request headers if provided
  if (req?.headers) {
    const headers = req.headers instanceof Headers ? req.headers : new Headers(req.headers as Record<string, string>);
    const forwardedProto = headers.get('x-forwarded-proto') || headers.get('x-forwarded-protocol');
    const forwardedHost = headers.get('x-forwarded-host');
    const host = headers.get('host');
    const proto = forwardedProto || (host?.startsWith('localhost') ? 'http' : 'https');
    const resolvedHost = forwardedHost || host;
    if (resolvedHost) {
      return normalizeBaseUrl(`${proto}://${resolvedHost}`);
    }
  }

  // Development fallback only
  if (process.env.NODE_ENV !== 'production') {
    return 'http://localhost:3000';
  }

  // Last resort in production to avoid throwing during generation
  // Prefer a safe https host; callers should still validate via isExternalUrlAllowed
  return 'https://app.scopax.io';
}

/**
 * Validate that a URL is externally accessible and HTTPS when required.
 * In production, disallow localhost/loopback and non-HTTPS links.
 */
export function isExternalUrlAllowed(url: string, env: 'development' | 'test' | 'production'): boolean {
  if (!url || typeof url !== 'string') return false;
  if (env === 'development' || env === 'test') return true;

  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase();
    const isLocal = host === 'localhost' || host === '127.0.0.1' || host === '::1';
    const isHttps = parsed.protocol === 'https:';
    return isHttps && !isLocal;
  } catch {
    return false;
  }
}
